import * as Sentry from '@sentry/react-native';
import { useEffect, useState } from 'react';

import { PSErrorClass } from '@/src/api/apiError';
import authApi from '@/src/api/authApi';
import { getAuthToken, setRestoredUser } from '@/src/lib/sessionStore';

/**
 * ── Cold-start restore ──────────────────────────────────────────────────
 *
 * A stored token outlives the process, so on every launch the app has a
 * credential and no idea who it belongs to — or whether it still works. One
 * `GET /me` answers both, behind the splash screen.
 *
 * This lives here rather than in [[sessionStore]] for one reason: it needs
 * `authApi`, which imports `api.ts`, which imports the store. The store has to
 * stay a leaf (see its docstring), so the one piece of session work that talks to
 * the network sits above it instead.
 *
 * ── Nothing here handles a dead token ──
 * A 401 on this call goes through the same interceptor as any other, which ends
 * the session — and an ended session is already what "boot signed out" means.
 * Every OTHER failure keeps the token (also the interceptor's rule), so a launch
 * in a tunnel boots signed-out for that launch only and the next cold start
 * restores them. Deleting on those would mean an app that logs you out for
 * opening it in a tunnel — a bug that looks exactly like a security feature,
 * which is why it survives so long in the wild.
 *
 * ── Also the pull-to-refresh `/me` ──
 * Exported because home's pull-to-refresh wants exactly this and nothing more:
 * re-read who the stored token belongs to. Same three properties carry over — no
 * token means no round trip (a signed-out pull costs nothing), a token that died
 * since launch ends the session through the same interceptor, and any other
 * failure leaves the session as it was. It REJECTS on those, unlike the gate
 * below which swallows; callers await it inside an `allSettled` or a `catch`.
 */
export async function restoreSession(): Promise<void> {
  const token = await getAuthToken();
  // No token: a first launch, or someone who signed out. Skip the round trip
  // rather than spend the splash screen's time on a call we know 401s.
  if (!token) return;

  setRestoredUser(await authApi.fetchMe());
}

/**
 * Run [[restoreSession]] once on mount and report when it's settled.
 *
 * The return value is the splash gate: false means "still deciding", and the
 * root layout renders nothing while it is. It flips exactly once and never goes
 * back. It reports only WHETHER the restore finished, not what it found —
 * consumers read the user from the store, so there is no second copy to seed.
 */
export function useRestoredSession(): boolean {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    restoreSession()
      // The gate MUST lift. An unhandled rejection here leaves `isReady` false
      // forever: the app sits on its splash screen with nothing on screen to say
      // why. A backstop is cheap; that failure is not diagnosable.
      .catch((error: unknown) => {
        console.warn('Session restore failed; booting signed out.', error);
        reportBootSignedOut(error);
      })
      .then(() => {
        if (!cancelled) setIsReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return isReady;
}

/**
 * A user who HAD a token and ended up on the signed-out app anyway.
 *
 * Three of the causes are deliberately silent, because they're either not bugs
 * or already reported: 'network' is a launch in a tunnel, which the docstring
 * above says boots signed out for that launch only; 'api' is the 401 of a
 * revoked token, which is a CORRECT sign-out; and 'server' is a 5xx that
 * `handleApiError` already filed against the endpoint, with a better fingerprint
 * than this site could give it.
 *
 * What's left is what nothing else in the app sees — a `/me` body we couldn't
 * read, and a raw throw, which here means a failed AsyncStorage read or the
 * profile mapping itself blowing up. `restoreSession` never passes through
 * `asApiError`, so those two reach Sentry from here or not at all.
 */
function reportBootSignedOut(error: unknown): void {
  const cause = error instanceof PSErrorClass ? error.kind : 'client-throw';
  if (cause !== 'unknown' && cause !== 'client-throw') return;

  Sentry.captureException(error, {
    fingerprint: ['session-restore-failed', cause],
    tags: { restore_failure_cause: cause },
    // Warning, not error: the gate lifted and the app booted. The user is
    // signed out, not stuck on a splash screen.
    level: 'warning',
  });
}
